// src/features/payment/RefundModal.tsx
// Full or partial refund for a captured payment.

import { useEffect, useState } from 'react';
import { useI18n } from '@react/hooks';
import { FormDialog, FormRow, SegmentedControl } from '@/components/form';
import { formatMoney, errMessage } from './format';

export interface RefundModalProps {
  open: boolean;
  maxAmount?: any; // Money — full refundable amount, for display only
  isSaving?: boolean;
  onCancel: () => void;
  onSubmit: (value: { full: boolean; amountCents: string; reason: string }) => Promise<void>;
}

export function RefundModal({ open, maxAmount, isSaving, onCancel, onSubmit }: RefundModalProps) {
  const { t } = useI18n();
  const [full, setFull] = useState(true);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setFull(true);
    setAmount('');
    setReason('');
    setError(null);
  }, [open]);

  const canSubmit = !!reason.trim() && (full || !!amount.trim());
  const dirty = !full || !!amount.trim() || !!reason.trim();

  const submit = async () => {
    if (!canSubmit || isSaving) return;
    setError(null);
    try {
      await onSubmit({ full, amountCents: amount.trim(), reason: reason.trim() });
    } catch (err) {
      setError(errMessage(err, t('payment.ledger.refundError')));
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onCancel}
      onSubmit={submit}
      submitLabel={t('payment.ledger.refund')}
      canSubmit={canSubmit}
      isSubmitting={isSaving}
      dirty={dirty}
      aria-label={t('payment.ledger.refund')}
    >
      <div className="space-y-3 px-3.5 pb-4 pt-3">
        {maxAmount && (
          <p className="text-sm text-muted-foreground">
            {t('payment.ledger.refundable')}: {formatMoney(maxAmount)}
          </p>
        )}
        {error && <p className="text-sm text-destructive">{error}</p>}

        <FormRow label={t('payment.ledger.refundType')} labelWidth="sm">
          <SegmentedControl
            aria-label={t('payment.ledger.refundType')}
            value={full ? 'full' : 'partial'}
            onChange={(v) => setFull(v === 'full')}
            options={[
              { value: 'full', label: t('payment.ledger.fullRefund') },
              { value: 'partial', label: t('payment.ledger.partialRefund') },
            ]}
          />
        </FormRow>

        {!full && (
          <FormRow label={t('payment.ledger.amount')} labelWidth="sm">
            <input
              data-autofocus
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputMode="decimal"
              placeholder="0.00"
              className="w-full rounded-md border border-input bg-background px-2.5 py-1 text-right text-sm tabular-nums text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </FormRow>
        )}

        <FormRow label={t('payment.ledger.reason')} labelWidth="sm">
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            autoCapitalize="off"
            className="w-full rounded-md border border-input bg-background px-2.5 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </FormRow>
      </div>
    </FormDialog>
  );
}

export default RefundModal;
